import { TrackingEvent } from 'client/tracking/event';
import { TrackingHelper } from 'client/tracking/maps/helper';
import { CommonMap } from 'client/tracking/maps/common';
import { DealerMap } from 'client/tracking/maps/common/dealer';
import { CalculatorMap } from 'client/tracking/maps/calculator';
import { AppraisalMap } from 'client/tracking/maps/appraisal';
import { TrackingConstant } from 'client/tracking/constant';
import { InventoryCommonMap } from 'client/tracking/maps/inventory/common';
import { VinHeartTrackingMap } from 'client/tracking/maps/inventory/common/vin-heart';
import { TrackingMap as DigitalRetailLandingMap } from 'client/tracking/maps/car-buying/dr-landing/index';
import { getSearchByModuleTrackingMap } from 'client/tracking/maps/common/search-by-module';
import { generateTrackingIds } from 'site-modules/shared/utils/make-model-zip';
import { EvInsights } from 'client/tracking/maps/inventory/ev-insights';
import { CaramelTrackingMap } from 'client/tracking/maps/financing/caramel';
import { IncentivesWizardMap } from 'client/tracking/maps/incentives-wizard';
import { PrivateListingMap } from 'client/tracking/maps/private-listing';

const PAGE_NAME = 'vla_used_model_car_inventory_vin_detail';
export const MMZ_TRACKING_IDS = generateTrackingIds(PAGE_NAME);

const map = {
  vdp_view_vin_highlights() {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_VIEW_VIN_DETAILS,
      TrackingConstant.VIEW_VIN_HIGHLIGHTS,
      TrackingHelper.getBasicDealerOptions()
    );
  },
  vdp_view_dealer_details(options) {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_DEALER_ENGAGEMENT,
      TrackingConstant.VIEW_DEALER_DETAILS,
      TrackingHelper.getBasicDealerOptions(options)
    );
  },
  vdp_view_content(options) {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_VIEW_CONTENT,
      TrackingHelper.getSubactionName(options)
    );
  },
  vdp_view_content_collapse: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_CONTENT,
    TrackingConstant.VIEW_DEALER_COMMENTS
  ),
  vdp_view_content_expand: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_CONTENT,
    TrackingConstant.VIEW_DEALER_COMMENTS
  ),
  vdp_call_dealer(options) {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_DEALER_ENGAGEMENT,
      TrackingConstant.CALL_DEALER,
      TrackingHelper.getBasicDealerOptions(options)
    );
  },
  free_experian() {
    return TrackingEvent.actionCompleted(TrackingConstant.ACTION_WEBSITE_CLICKOUT, TrackingConstant.EXPERIAN_FREE_VHR);
  },
  paid_experian() {
    return TrackingEvent.actionCompleted(TrackingConstant.ACTION_WEBSITE_CLICKOUT, TrackingConstant.EXPERIAN_PAID_VHR);
  },
  view_vehicle(options) {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_VIEW_VIN_DETAILS,
      TrackingConstant.ACTION_VIEW_VIN_DETAILS,
      TrackingHelper.getBasicDealerOptions({ ...options, setPartnerId: true })
    );
  },
  dealer_site_clickout(options) {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_WEBSITE_CLICKOUT,
      TrackingConstant.DEALER_SITE_CLICKOUT,
      TrackingHelper.getBasicDealerOptions({ ...options, setPartnerId: true })
    );
  },
  appraisal_retrieval(options) {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_APPRAISAL_RETRIEVAL,
      TrackingConstant.ACTION_APPRAISAL_RETRIEVAL,
      options
    );
  },
  open_carcode_sms(options) {
    return TrackingEvent.actionStart(
      TrackingConstant.ACTION_LEAD_SUBMISSION,
      TrackingConstant.DISPLAY_SALES_LEAD_FORM,
      TrackingHelper.getBasicDealerOptions({ ...options, setPartnerId: true })
    );
  },
  ddp_go_to_review_form: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_WRITE_REVIEW,
    TrackingConstant.GO_TO_REVIEW_FORM
  ),
  vdp_view_inventory: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_DEALER_ENGAGEMENT,
    TrackingConstant.VIEW_DEALER_INVENTORY
  ),
  vdp_preapproved_loan: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_TOOL_SELECT,
    TrackingConstant.GET_PREAPPROVED_LOAN
  ),
  vdp_view_dealer_reviews: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_DEALER_ENGAGEMENT,
    TrackingConstant.VIEW_DEALER_REVIEWS
  ),
  vdp_view_vin_details: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_VIN_DETAILS,
    TrackingConstant.ACTION_VIEW_VIN_DETAILS
  ),
  vdp_tool_select: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_TOOL_SELECT,
    TrackingConstant.ACTION_TOOL_SELECT
  ),
  vdp_view_search_result: TrackingEvent.actionCompleted(
    TrackingConstant.VIEW_SEARCH_RESULT,
    TrackingConstant.VIEW_SEARCH_RESULT
  ),
  open_buyback_protection_website: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_WEBSITE_CLICKOUT,
    TrackingConstant.BUYBACK_PROTECTION
  ),
  open_texas_dmv_contact_website: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_WEBSITE_CLICKOUT,
    TrackingConstant.TEXAS_DMV_CONTACT
  ),
  sell_your_car_clickout: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_WEBSITE_CLICKOUT,
    TrackingConstant.SELL_YOUR_CAR
  ),
  view_content_esp: TrackingEvent.actionCompleted(TrackingConstant.ACTION_VIEW_CONTENT, TrackingConstant.VIEW_ESP),
  view_content_estimate: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_CONTENT,
    TrackingConstant.VIEW_ESTIMATE
  ),
  read_all_reviews: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_CONTENT,
    TrackingConstant.READ_ALL_REVIEWS
  ),
  reviews_specs: TrackingEvent.actionCompleted(TrackingConstant.ACTION_VIEW_CONTENT, TrackingConstant.REVIEW_SPECS),
  filter_consumer_review_aspect: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_REVIEW,
    TrackingConstant.SEARCH_REVIEW_TOPIC,
    {
      action_cause: 'image_click',
      value: 'view_subcategory_info',
    }
  ),
  see_something_wrong: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_WEBSITE_CLICKOUT,
    TrackingConstant.SEE_SOMETHING_WRONG
  ),
  payment_feedback: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_WEBSITE_CLICKOUT,
    TrackingConstant.PAYMENT_FEEDBACK
  ),
  consumer_review_aspect_expand: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_RESET_NAVIGATION,
    TrackingConstant.VIEW_MORE
  ),
  consumer_review_aspect_collapse: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_RESET_NAVIGATION,
    TrackingConstant.VIEW_LESS
  ),
  tmv_esp_mismatch_payment_show: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_SHOW_CONTENT,
    TrackingConstant.SHOW_TMV_ESP_MISMATCH_PAYMENT,
    {
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
    }
  ),
  view_cpo_section: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_CONTENT,
    TrackingConstant.VIEW_CPO_SECTION
  ),
  show_saved_vin_count(options) {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_SHOW_CONTENT,
      TrackingConstant.SHOW_SAVE_VIN_COUNT,
      options
    );
  },
  show_viewed_vin_count(options) {
    return TrackingEvent.actionCompleted(
      TrackingConstant.ACTION_SHOW_CONTENT,
      TrackingConstant.SHOW_VIEWED_VIN_COUNT,
      options
    );
  },
  select_incentives: TrackingEvent.actionProgress(
    TrackingConstant.ACTION_PURCHASE_VEHICLE,
    TrackingConstant.SELECT_INCENTIVES
  ),
  view_more_info: TrackingEvent.actionCompleted(TrackingConstant.ACTION_VIEW_CONTENT, TrackingConstant.VIEW_MORE_INFO),

  view_price_history: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_CONTENT,
    TrackingConstant.VIEW_PRICE_HISTORY
  ),

  see_recall_info: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_WEBSITE_CLICKOUT,
    TrackingConstant.SEE_RECALL_INFO
  ),

  view_360: TrackingEvent.actionCompleted(TrackingConstant.ACTION_VIEW_CONTENT, TrackingConstant.VIEW_360),
  view_photo: TrackingEvent.actionCompleted(TrackingConstant.ACTION_VIEW_PHOTO, TrackingConstant.ACTION_VIEW_PHOTO),

  request_information: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_PRICING_DETAILS,
    TrackingConstant.REQUEST_INFORMATION
  ),

  [MMZ_TRACKING_IDS.makeSelectTrackingId]: TrackingEvent.actionStart(
    TrackingConstant.ACTION_MMY_SELECT,
    TrackingConstant.MAKE_SELECT
  ),

  [MMZ_TRACKING_IDS.modelSelectTrackingId]: TrackingEvent.actionProgress(
    TrackingConstant.ACTION_MMY_SELECT,
    TrackingConstant.MODEL_SELECT
  ),

  [MMZ_TRACKING_IDS.submitTrackingId]: TrackingEvent.actionEnd(
    TrackingConstant.ACTION_MMY_SELECT,
    TrackingConstant.SUBMIT_MMY
  ),

  ...getSearchByModuleTrackingMap(MMZ_TRACKING_IDS),

  bol_btn_click: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_CONTENT,
    TrackingConstant.BUY_ONLINE_CLICKOUT
  ),
  check_availability: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_PROVIDE_ZIP_CODE,
    TrackingConstant.CHECK_AVAILABILITY
  ),
  search: TrackingEvent.actionCompleted(TrackingConstant.ACTION_PROVIDE_ZIP_CODE, TrackingConstant.SEARCH),
  open_financing_app: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_FILL_FORM,
    TrackingConstant.SUBACTION_OPEN_FINANCING_APP
  ),

  // shop-4713-prequal2 test
  financing_cta_lead_form: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_FILL_FORM,
    TrackingConstant.SUBACTION_OPEN_FINANCING_LEAD_FORM,
    {
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
    }
  ),

  vdp_dealer_fees_button_click: TrackingEvent.actionCompleted(
    TrackingConstant.ACTION_VIEW_CONTENT,
    TrackingConstant.ACTION_VIEW_CONTENT
  ),
};

export const TrackingMap = {
  ...CommonMap,
  ...DealerMap,
  ...CalculatorMap,
  ...InventoryCommonMap,
  ...VinHeartTrackingMap,
  ...AppraisalMap,
  ...DigitalRetailLandingMap,
  ...EvInsights,
  ...CaramelTrackingMap,
  ...IncentivesWizardMap,
  ...PrivateListingMap,
  ...map,
};
